import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { DndContext } from '@dnd-kit/core';
import { SortableContext } from '@dnd-kit/sortable';
import {
  Comic,
  ComicSeries,
  byReadingOrder,
  checklistSize,
  completeLabel,
  progressPercent,
  seriesProgress,
  unitLabel,
} from '../types';
import { gradients } from '../../../theme/colors';
import CoverImage from './CoverImage';
import ComicCard from './ComicCard';
import IssueChecklist from './IssueChecklist';

interface SeriesDialogProps {
  series: ComicSeries | null;
  comics: Comic[];
  coverUrl?: string;
  groupName?: string;
  isReadOnly: boolean;
  onClose: () => void;
  onEditSeries: (series: ComicSeries) => void;
  onToggleComplete: (series: ComicSeries) => void;
  onToggleWishlist: (series: ComicSeries) => void;
  onMenuClick: (e: React.MouseEvent<HTMLButtonElement>, comic: Comic) => void;
  onToggleRead: (comic: Comic) => void;
  onToggleOwned: (comic: Comic) => void;
  onToggleFavourite: (comic: Comic) => void;
  onToggleNumber: (series: ComicSeries, number: number) => void;
  onMarkRange: (series: ComicSeries, upTo: number) => void;
}

// A series' header and progress, its checklist, then its covers in reading order.
const SeriesDialog = ({
  series,
  comics,
  coverUrl,
  groupName,
  isReadOnly,
  onClose,
  onEditSeries,
  onToggleComplete,
  onToggleWishlist,
  onMenuClick,
  onToggleRead,
  onToggleOwned,
  onToggleFavourite,
  onToggleNumber,
  onMarkRange,
}: SeriesDialogProps) => {
  const [showCovers, setShowCovers] = useState(false);
  const seriesId = series?.id;
  const coverCount = comics.filter((comic) => comic.seriesId === seriesId).length;

  // Short runs open with their covers showing; long ones lead with the checklist
  useEffect(() => {
    setShowCovers(coverCount > 0 && coverCount <= 12);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seriesId]);

  if (!series) return null;

  const inSeries = comics.filter((comic) => comic.seriesId === series.id).sort(byReadingOrder);
  const { read, owned, total } = seriesProgress(series, comics);
  const percent = progressPercent(read, total);
  const unit = unitLabel(series.trackBy, total);
  const numberPrefix = series.trackBy === 'issue' ? '#' : 'Vol. ';
  const size = checklistSize(series, comics);
  const complete = Boolean(series.complete);
  const completeText = completeLabel(series);
  const wishlisted = Boolean(series.wishlisted);

  // On: gradient. Off: a quiet outline inviting the tap
  const toggleChipSx = (on: boolean) => ({
    fontWeight: 700,
    ...(on
      ? {
          backgroundImage: gradients.primary,
          color: '#ffffff',
          '& .MuiChip-icon': { color: '#ffffff' },
          '&:hover': { filter: 'brightness(1.08)' },
        }
      : { borderColor: 'divider', color: 'text.secondary' }),
  });

  const details = [
    series.publisher,
    series.startYear ? String(series.startYear) : '',
    series.volumeNumber ? `Vol. ${series.volumeNumber}` : '',
    series.ongoing ? 'Ongoing' : '',
    groupName ? `In ${groupName}` : '',
  ].filter(Boolean);

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        {series.title}
        {series.startYear ? ` (${series.startYear})` : ''}
        <IconButton
          aria-label="Close"
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ display: 'flex', gap: 2.5, alignItems: 'flex-start', mb: 3 }}>
          <Box sx={{ width: { xs: 90, sm: 120 }, flexShrink: 0, borderRadius: 1, overflow: 'hidden' }}>
            <CoverImage src={coverUrl} title={series.title} />
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {details.join(' · ')}
            </Typography>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {total > 0 ? `${read} / ${total} ${unit} read` : 'Nothing logged yet'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {owned} owned
              </Typography>
            </Box>
            <Box sx={{ height: 8, borderRadius: 4, bgcolor: 'action.hover', overflow: 'hidden' }}>
              <Box
                sx={{
                  width: `${percent}%`,
                  height: '100%',
                  backgroundImage: gradients.primary,
                  backgroundSize: percent > 0 ? `${(100 / percent) * 100}% 100%` : undefined,
                }}
              />
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
              {(!isReadOnly || complete) && (
                <Chip
                  size="small"
                  icon={<CheckIcon />}
                  label={complete ? completeText : `Mark ${completeText.toLowerCase()}`}
                  clickable={!isReadOnly}
                  onClick={isReadOnly ? undefined : () => onToggleComplete(series)}
                  aria-pressed={isReadOnly ? undefined : complete}
                  variant={complete ? 'filled' : 'outlined'}
                  sx={toggleChipSx(complete)}
                />
              )}
              {(!isReadOnly || wishlisted) && (
                <Chip
                  size="small"
                  icon={wishlisted ? <BookmarkIcon /> : <BookmarkBorderIcon />}
                  label={wishlisted ? 'On Wishlist' : 'Add to Wishlist'}
                  clickable={!isReadOnly}
                  onClick={isReadOnly ? undefined : () => onToggleWishlist(series)}
                  aria-pressed={isReadOnly ? undefined : wishlisted}
                  variant={wishlisted ? 'filled' : 'outlined'}
                  sx={toggleChipSx(wishlisted)}
                />
              )}
            </Box>
          </Box>
        </Box>

        <IssueChecklist
          series={series}
          comics={inSeries}
          size={size}
          isReadOnly={isReadOnly}
          onToggleNumber={onToggleNumber}
          onMarkRange={onMarkRange}
        />

        {inSeries.length > 0 && (
          <Box sx={{ mt: 2.5 }}>
            <Button
              onClick={() => setShowCovers((prev) => !prev)}
              aria-expanded={showCovers}
              endIcon={
                <ExpandMoreIcon
                  sx={{ transition: 'transform 0.2s ease', transform: showCovers ? 'rotate(180deg)' : 'none' }}
                />
              }
              sx={{ color: 'text.secondary', px: 1, ml: -1 }}
            >
              Covers ({inSeries.length})
            </Button>

            <Collapse in={showCovers} unmountOnExit>
              <DndContext>
                <SortableContext items={inSeries.map((comic) => comic.id)}>
                  <Box
                    sx={{
                      pt: 1.5,
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: 'repeat(2, minmax(0, 1fr))',
                        sm: 'repeat(auto-fill, minmax(150px, 1fr))',
                      },
                      alignItems: 'start',
                      gap: 2,
                    }}
                  >
                    {inSeries.map((comic) => (
                      <ComicCard
                        key={comic.id}
                        comic={comic}
                        subtitle={comic.number != null ? `${numberPrefix}${comic.number}` : ''}
                        isReadOnly={isReadOnly}
                        onMenuClick={onMenuClick}
                        onToggleRead={onToggleRead}
                        onToggleOwned={onToggleOwned}
                        onToggleFavourite={onToggleFavourite}
                      />
                    ))}
                  </Box>
                </SortableContext>
              </DndContext>
            </Collapse>
          </Box>
        )}
      </DialogContent>

      {!isReadOnly && (
        <DialogActions sx={{ justifyContent: 'space-between' }}>
          <Button onClick={() => onEditSeries(series)}>Edit series</Button>
          <Button color="inherit" onClick={onClose}>
            Done
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default SeriesDialog;
